// 📄 pages/Proyectos/components/CasoPruebaCard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Pencil, AlertCircle, ExternalLink, FileText } from 'lucide-react';
import { CasoPrueba, CasoPruebaEstado, CASO_PRUEBA_ESTADOS } from '../../../services/casoPruebaService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';

const ESTADO_BADGE: Record<CasoPruebaEstado, string> = {
  'Pendiente': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  'Aprobado': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Fallido': 'bg-red-500/10 text-red-300 border-red-500/20'
};

interface CasoPruebaCardProps {
  caso: CasoPrueba;
  projectId: string;
  // Editar nombre/datos/detalles: solo quien lo creó (o admin/owner-admin
  // de org). Cambiar el estado (ejecutar el caso): cualquier miembro no
  // cliente — son permisos independientes, ver CasosPruebaSection.tsx.
  puedeEditarContenido: boolean;
  puedeCambiarEstado: boolean;
  onEditar: () => void;
  onEstadoChange: (estado: CasoPruebaEstado) => void;
  onCrearIncidente: () => void;
}

export default function CasoPruebaCard({
  caso, projectId, puedeEditarContenido, puedeCambiarEstado, onEditar, onEstadoChange, onCrearIncidente
}: CasoPruebaCardProps) {
  // Casos creados antes de que existiera la función de adjuntos no tienen
  // el campo en Firestore (Firestore no hace backfill de campos nuevos) —
  // sin el fallback, `.filter` sobre undefined tira TypeError.
  const adjuntos = caso.adjuntos || [];
  const imagenes = adjuntos.filter((a) => a.contentType.startsWith('image/'));
  const documentos = adjuntos.filter((a) => !a.contentType.startsWith('image/'));

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">{caso.nombre}</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {SOLUCIONES[caso.solucionRelacionada]?.label || caso.solucionRelacionada}
            {caso.canalNombre ? ` · ${caso.canalNombre}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_BADGE[caso.estado]}`}>{caso.estado}</span>
          {puedeEditarContenido && (
            <button onClick={onEditar} className="text-gray-400 hover:text-white">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {caso.campos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {caso.campos.map((campo, i) => (
            <span key={i} className="text-xs px-2 py-1 rounded-lg bg-black/20 border border-white/10 text-gray-300">
              <span className="text-gray-500">{campo.nombre}:</span> {campo.valor}
            </span>
          ))}
        </div>
      )}

      {caso.detalles && <p className="text-sm text-gray-300 whitespace-pre-wrap">{caso.detalles}</p>}

      {imagenes.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {imagenes.map((img) => (
            <a key={img.url} href={img.url} target="_blank" rel="noopener noreferrer">
              <img src={img.url} alt={img.name} className="w-full rounded-lg border border-white/10 hover:border-blue-400 transition-all" />
            </a>
          ))}
        </div>
      )}
      {documentos.length > 0 && (
        <div className="space-y-1.5">
          {documentos.map((d) => (
            <a
              key={d.url} href={d.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300 hover:border-blue-400 transition-all"
            >
              <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
              <span className="truncate">{d.name}</span>
            </a>
          ))}
        </div>
      )}

      {caso.estado === 'Aprobado' && caso.detalleAprobacion && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 space-y-1.5">
          <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Resultado</p>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{caso.detalleAprobacion.resultado}</p>
          {caso.detalleAprobacion.consideraciones && (
            <p className="text-xs text-gray-400 whitespace-pre-wrap">{caso.detalleAprobacion.consideraciones}</p>
          )}
        </div>
      )}

      {caso.estado === 'Fallido' && caso.detalleFallo && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 space-y-2">
          <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">{caso.detalleFallo.tipoError}</p>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{caso.detalleFallo.descripcion}</p>
          {caso.incidenteId ? (
            <Link
              to={`/proyecto/${projectId}/incidentes?incidenteId=${caso.incidenteId}`}
              className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 w-fit"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Ver incidente vinculado
            </Link>
          ) : puedeCambiarEstado && (
            <button
              onClick={onCrearIncidente}
              className="flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200 w-fit"
            >
              <AlertCircle className="w-3.5 h-3.5" /> Crear incidente a partir de este fallo
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1 border-t border-white/10">
        <p className="text-xs text-gray-500">
          {caso.creadoPorNombre}
          {caso.ejecutadoPorNombre ? ` · última ejecución: ${caso.ejecutadoPorNombre}` : ''}
        </p>
        {puedeCambiarEstado && (
          <select
            value={caso.estado}
            onChange={(e) => onEstadoChange(e.target.value as CasoPruebaEstado)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
          >
            {CASO_PRUEBA_ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}
